import * as React from 'react'
import * as t from './types'
import theme from 'theme'
import styled from 'styled-components'
import CatImage from './CatImage'
import EecTracking from 'theme/atoms/EecTracking'
import { getMediaSize } from 'utils/media-size'
import useArrowNavigation from 'hooks/useSliderArrowNavigation'
import PositionIndicator from 'theme/molecules/ProductSlider/PositionIndicator'
import SliderButton from 'theme/atoms/SliderButton'

/**
 * Shows a slider with category images and links
 * @param props: Props like UserConfig & Context from types.ts
 * @returns CategoryLinkSlider component
 */
export default function CategoryLinkSlider(props: t.Props) {
  const isMobile = getMediaSize() === 'XS' || getMediaSize() === 'SM'
  const contextCategories = isMobile
    ? props.context.categories.slice(0, 7)
    : props.context.categories
  const {
    lastItemRef,
    firstItemRef,
    productListRef,
    lastItemReached,
    firstItemReached,
    handleSlide
  } = useArrowNavigation(0.8)

  return (
    <EecTracking config={props.eecTracking} gridArea={props.gridArea}>
      <Wrapper data-cy-ctx="organisms/CategoryLinkSlider">
        <div className="headlineAndImages">
          <div className="custom-headline">
            <h2 className="headline-h2">{props.title}</h2>
          </div>
          <div className="inner-slider">
            <div className="image-box" ref={productListRef}>
              <Indicator ref={firstItemRef} $width={1} />
              {contextCategories.map((cat, i) => {
                return (
                  <CatImage
                    key={i}
                    label={cat.label}
                    link={cat.link}
                    optImg={props.context.optImg[i]}
                  />
                )
              })}
              <Indicator ref={lastItemRef} $width={1} />
            </div>
            {!isMobile && !firstItemReached && (
              <SliderButton
                facing="left"
                onClick={() => handleSlide('left')}
                data-cy-handle="prev-button"
                data-cy-state="has-prev-button"
                title="Previous Category"
              />
            )}
            {!isMobile && !lastItemReached && (
              <SliderButton
                facing="right"
                onClick={() => handleSlide('right')}
                data-cy-handle="next-button"
                data-cy-state="has-next-button"
                title="Next Category"
              />
            )}
          </div>
          <PositionIndicator
            data-cy-state="show-position-indicator"
            componentRef={productListRef}
            maxSteps={contextCategories.length}
            offset={1}
            displayedSteps={contextCategories.length}
          />
        </div>
      </Wrapper>
    </EecTracking>
  )
}

const Wrapper = styled.div`
  width: 100%;
  max-width: var(--container-size);
  > .headlineAndImages {
    > .custom-headline {
      margin-bottom: ${theme.spacing('s')};
      
      > .headline-h2 {
        ${theme.ty('rc-2xl', null, 'rc-3xl')};
        color: ${theme.colors.b0};
        text-align: left;
        margin: 0;
        padding: 0;
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
      }
    }

    > .inner-slider {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      > .image-box {
        width: 100%;
        display: flex;
        flex-direction: column;
        overflow-y: scroll;
        scrollbar-width: none;
        ::-webkit-scrollbar {
          background: transparent;
        }
        > .CatImage:first-child {
          margin-top: 0 !important;
        }
        > .CatImage {
          margin-top: 0;
          margin-bottom: 0;
        }
        @media (min-width: ${theme.ms.SM}px) {
          flex-direction: row;
          gap: ${theme.spacing('m')};
          overflow-y: hidden;
          scrollbar-width: none;
          ::-webkit-scrollbar {
            background: transparent;
          }
        }
      }
    }
    > .PositionIndicator {
      display: none;
      @media (min-width: ${theme.ms.SM}px) {
        display: block;
      }
    }
  }
`

const Indicator = styled.div<{ $width: number }>`
  width: ${(p) => p.$width + 'px'};
  min-width: ${(p) => p.$width + 'px'};
  height: 0.0625rem;
`
